import { useState, useEffect, useRef } from "react";
import { bindActionCreators } from "@reduxjs/toolkit";
import { connect } from "react-redux";
import productActions from "../../../store/actions/items/item.actions";
import categoryActions from "../../../store/actions/items/categories/category.actions";
import itemGroupsActions from "../../../store/actions/items/categories/itemGroups/itemGroups.actions";
import catalogActions from "../../../store/actions/catalog/catalog.actions";
import TableElementComponent from "../../../components/general/elements/TableElementComponent";
import TextInputElementComponent from "../../../components/general/elements/inputElements/TextInputElementComponent";
import textInputModel from "../../../models/general/elements/inputElements/textInput.model";
import "../../../styles/pages/dashboard/adminCatalogManagementPageComponent.scss";

export const AdminCatalogManagementPageComponent = ({ productActions, categoryActions, itemGroupsActions, catalogActions, items, categories, itemGroups, catalogLoading }) => {
  const [activeTab, setActiveTab] = useState("items"); // 'items', 'categories', 'itemGroups'
  const [selectedItems, setSelectedItems] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedItemGroups, setSelectedItemGroups] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const lastFetchedTabRef = useRef(null);

  // Load data on component mount and tab change - only when tab changes
  useEffect(() => {
    // Only fetch if tab changed
    if (activeTab === "items" && lastFetchedTabRef.current !== "items") {
      lastFetchedTabRef.current = "items";
      // Check if we need to fetch (data is empty)
      const needsFetch = !items || items.length === 0;
      if (needsFetch) {
        productActions.getItems({ pageNumber: 1, pageSize: 1000, getAll: true });
      }
    } else if (activeTab === "categories" && lastFetchedTabRef.current !== "categories") {
      lastFetchedTabRef.current = "categories";
      const needsFetch = !categories || categories.length === 0;
      if (needsFetch) {
        categoryActions.getCategories({ pageNumber: 1, pageSize: 1000 });
      }
    } else if (activeTab === "itemGroups" && lastFetchedTabRef.current !== "itemGroups") {
      lastFetchedTabRef.current = "itemGroups";
      const needsFetch = !itemGroups || itemGroups.length === 0;
      if (needsFetch) {
        itemGroupsActions.getItemGroups({ pageNumber: 1, pageSize: 1000 });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const handleItemToggle = (itemId) => {
    setSelectedItems((prev) => (prev.includes(itemId) ? prev.filter((id) => id !== itemId) : [...prev, itemId]));
  };

  const handleGenerateCatalog = async () => {
    try {
      if (activeTab === "items" && selectedItems.length > 0) {
        await catalogActions.exportCatalogByItems(selectedItems);
      } else if (activeTab === "categories" && selectedCategories.length > 0) {
        await catalogActions.exportCatalogByCategories(selectedCategories);
      } else if (activeTab === "itemGroups" && selectedItemGroups.length > 0) {
        await catalogActions.exportCatalogByItemGroups(selectedItemGroups);
      } else {
        alert("אנא בחר לפחות פריט אחד");
      }
    } catch (error) {
      console.error("Error generating catalog:", error);
      alert("אירעה שגיאה ביצירת הקטלוג");
    }
  };

  const handleCategoryToggle = (categoryId) => {
    setSelectedCategories((prev) => (prev.includes(categoryId) ? prev.filter((id) => id !== categoryId) : [...prev, categoryId]));
  };

  const handleItemGroupToggle = (itemGroupId) => {
    setSelectedItemGroups((prev) => (prev.includes(itemGroupId) ? prev.filter((id) => id !== itemGroupId) : [...prev, itemGroupId]));
  };

  const handleSelectAll = () => {
    if (activeTab === "items") {
      const allIds = filteredItems.map((item) => item.id || item.Id).filter(Boolean);
      setSelectedItems(allIds);
    } else if (activeTab === "categories") {
      const allIds = filteredCategories.map((cat) => cat.id || cat.Id).filter(Boolean);
      setSelectedCategories(allIds);
    } else if (activeTab === "itemGroups") {
      const allIds = filteredItemGroups.map((ig) => ig.id || ig.Id).filter(Boolean);
      setSelectedItemGroups(allIds);
    }
  };

  const handleDeselectAll = () => {
    if (activeTab === "items") {
      setSelectedItems([]);
    } else if (activeTab === "categories") {
      setSelectedCategories([]);
    } else if (activeTab === "itemGroups") {
      setSelectedItemGroups([]);
    }
  };

  // Filter data based on search term
  const filteredItems = items?.filter((item) => !searchTerm || item.itemDescription?.toLowerCase().includes(searchTerm.toLowerCase()) || item.itemId?.toString().includes(searchTerm)) || [];

  const filteredCategories = categories?.filter((cat) => !searchTerm || cat.categoryName?.toLowerCase().includes(searchTerm.toLowerCase())) || [];

  const filteredItemGroups = itemGroups?.filter((ig) => !searchTerm || ig.itemGroupName?.toLowerCase().includes(searchTerm.toLowerCase())) || [];

  const getItemsTable = () => {
    const columns = [
      {
        label: "",
        field: "id",
        render: (value, row) => <input type="checkbox" checked={selectedItems.includes(row.id)} onChange={() => handleItemToggle(row.id)} />,
      },
      {
        label: "תמונה",
        field: "pictureUrl",
        render: (value, row) => (
          <div className="imageColumnContainer">
            {value ? (
              <img
                src={value}
                alt={row.itemDescription}
                onError={(e) => {
                  e.target.style.display = "none";
                  e.target.nextElementSibling.style.display = "flex";
                }}
              />
            ) : null}
            <div className="no-image-placeholder" style={{ display: value ? "none" : "flex" }}>
              <i className="fa-solid fa-image"></i>
            </div>
          </div>
        ),
      },
      { label: "קוד פריט", field: "itemId" },
      { label: "שם פריט", field: "itemDescription" },
      { label: "מחיר (₪)", field: "salePriceInNIS" },
    ];

    return <TableElementComponent columns={columns} data={filteredItems} />;
  };

  const getCategoriesTable = () => {
    const columns = [
      {
        label: "",
        field: "id",
        render: (value, row) => <input type="checkbox" checked={selectedCategories.includes(row.id)} onChange={() => handleCategoryToggle(row.id)} />,
      },
      {
        label: "תמונה",
        field: "imageURL",
        render: (value, row) => (
          <div className="imageColumnContainer">
            {value ? (
              <img
                src={value}
                alt={row.categoryName}
                onError={(e) => {
                  e.target.style.display = "none";
                  e.target.nextElementSibling.style.display = "flex";
                }}
              />
            ) : null}
            <div className="no-image-placeholder" style={{ display: value ? "none" : "flex" }}>
              <i className="fa-solid fa-image"></i>
            </div>
          </div>
        ),
      },
      { label: "שם קטגוריה", field: "categoryName" },
      { label: "תיאור", field: "description" },
    ];

    return <TableElementComponent columns={columns} data={filteredCategories} />;
  };

  const getItemGroupsTable = () => {
    const columns = [
      {
        label: "",
        field: "id",
        render: (value, row) => <input type="checkbox" checked={selectedItemGroups.includes(row.id)} onChange={() => handleItemGroupToggle(row.id)} />,
      },
      {
        label: "תמונה",
        field: "pictureUrl",
        render: (value, row) => (
          <div className="imageColumnContainer">
            {value ? (
              <img
                src={value}
                alt={row.itemGroupName}
                onError={(e) => {
                  e.target.style.display = "none";
                  e.target.nextElementSibling.style.display = "flex";
                }}
              />
            ) : null}
            <div className="no-image-placeholder" style={{ display: value ? "none" : "flex" }}>
              <i className="fa-solid fa-image"></i>
            </div>
          </div>
        ),
      },
      { label: "שם קבוצת פריטים", field: "itemGroupName" },
      { label: "קוד קבוצה", field: "itemGroupId" },
    ];

    return <TableElementComponent columns={columns} data={filteredItemGroups} />;
  };

  const getSearchInput = () => {
    const inputProps = {
      ...textInputModel,
      name: "searchInput",
      value: searchTerm,
      placeholder: "חפש...",
      onChange: (e) => setSearchTerm(e.target.value),
      icon: <i className="fa-solid fa-magnifying-glass"></i>,
    };
    return <TextInputElementComponent props={inputProps} />;
  };

  const getSelectedCount = () => {
    if (activeTab === "items") return selectedItems.length;
    if (activeTab === "categories") return selectedCategories.length;
    if (activeTab === "itemGroups") return selectedItemGroups.length;
    return 0;
  };

  return (
    <div className="adminCatalogManagementContainer">
      <div className="catalogHeader">
        <h1>ניהול קטלוג</h1>
        <p className="catalogDescription">בחר פריטים, קטגוריות או קבוצות פריטים ליצירת קטלוג PDF</p>
      </div>

      {/* Tabs */}
      <div className="catalogTabs">
        <button className={`catalogTab ${activeTab === "items" ? "active" : ""}`} onClick={() => setActiveTab("items")}>
          <i className="fa-solid fa-box"></i> פריטים
        </button>
        <button className={`catalogTab ${activeTab === "categories" ? "active" : ""}`} onClick={() => setActiveTab("categories")}>
          <i className="fa-solid fa-folder-tree"></i> קטגוריות
        </button>
        <button className={`catalogTab ${activeTab === "itemGroups" ? "active" : ""}`} onClick={() => setActiveTab("itemGroups")}>
          <i className="fa-solid fa-layer-group"></i> קבוצות פריטים
        </button>
      </div>

      <div className="catalogContentWrapper">
        {/* Search and Selection Controls */}
        <div className="catalogControls">
          <div className="searchContainer">{getSearchInput()}</div>
          <div className="selectionControls">
            <button className="primaryButton" onClick={handleSelectAll}>
              בחר הכל
            </button>
            <button className="primaryButton" onClick={handleDeselectAll}>
              בטל בחירה
            </button>
          </div>
        </div>

        {/* Selection Summary */}
        <div className="selectionSummary">
          <p>
            נבחרו: <strong>{getSelectedCount()}</strong> {activeTab === "items" ? "פריטים" : activeTab === "categories" ? "קטגוריות" : "קבוצות פריטים"}
          </p>
        </div>

        {/* Table Content */}
        <div className="catalogTableContainer">
          {activeTab === "items" && getItemsTable()}
          {activeTab === "categories" && getCategoriesTable()}
          {activeTab === "itemGroups" && getItemGroupsTable()}
        </div>

        {/* Generate Button */}
        <div className="generateButtonContainer">
          <button className="primaryButton generateButton" onClick={handleGenerateCatalog} disabled={getSelectedCount() === 0 || catalogLoading}>
            {catalogLoading ? (
              <>
                <i className="fa-solid fa-spinner fa-spin"></i> מייצר קטלוג...
              </>
            ) : (
              <>
                <i className="fa-solid fa-file-pdf"></i> צור קטלוג PDF
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  items: state.productReducer?.products || [],
  categories: state.categoryReducer?.categories?.items || [],
  itemGroups: state.itemGroupsReducer?.itemGroups?.items || [],
  catalogLoading: state.catalogReducer?.loading || false,
});

const mapDispatchToProps = (dispatch) => ({
  productActions: bindActionCreators(productActions, dispatch),
  categoryActions: bindActionCreators(categoryActions, dispatch),
  itemGroupsActions: bindActionCreators(itemGroupsActions, dispatch),
  catalogActions: bindActionCreators(catalogActions, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(AdminCatalogManagementPageComponent);
